import { useState, useMemo } from 'react';
import { DatePicker, Button, Space, Switch, message, Empty } from 'antd';
import { ReloadOutlined, FileExcelOutlined, TableOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import type { ColDef } from 'ag-grid-community';
import ExcelJS from 'exceljs';
import PivotTableUI from 'react-pivottable/PivotTableUI';
import type { PivotTableUIProps } from 'react-pivottable/PivotTableUI';
import 'react-pivottable/pivottable.css';
import { DataGrid } from './DataGrid';
import { TierBadge } from './TierBadge';
import { queryDataSource } from '../api/query';
import type { ReportConfig, ColumnConfig } from '../types/panelConfig';

const { RangePicker } = DatePicker;

const currencyFormatter = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 });
const numberFormatter = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const percentFormatter = new Intl.NumberFormat('tr-TR', { style: 'percent', minimumFractionDigits: 2 });

function formatValue(col: ColumnConfig, value: unknown): string {
  if (value == null) return '';
  if (col.cellRenderer === 'currency' || col.type === 'currency') return currencyFormatter.format(Number(value));
  if (col.cellRenderer === 'percentage' || col.type === 'percentage') return percentFormatter.format(Number(value) / 100);
  if (col.type === 'number') return numberFormatter.format(Number(value));
  if (col.type === 'date' && col.format) return dayjs(String(value)).format(col.format);
  return String(value);
}

function getCellValueFormatter(col: ColumnConfig) {
  if (col.type === 'currency' || col.cellRenderer === 'currency' ||
      col.type === 'percentage' || col.cellRenderer === 'percentage' ||
      col.type === 'number') {
    return (params: { value: unknown }) => formatValue(col, params.value);
  }
  if (col.type === 'date' && col.format) {
    return (params: { value: unknown }) => formatValue(col, params.value);
  }
  return undefined;
}

function buildAgGridColumns(columns: ColumnConfig[]): ColDef[] {
  return columns.map((col) => {
    const isNumeric = col.type === 'currency' || col.type === 'number' || col.type === 'percentage';
    return {
      field: col.field,
      headerName: col.header,
      width: col.width || undefined,
      pinned: col.pinned ? ('left' as const) : undefined,
      filter: col.filterable,
      sortable: true,
      resizable: true,
      valueFormatter: getCellValueFormatter(col),
      aggFunc: col.agg || undefined,
      cellClass: isNumeric ? 'tabular ag-right' : undefined,
      cellStyle: isNumeric
        ? { textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontFeatureSettings: "'tnum'" }
        : undefined,
      headerClass: isNumeric ? 'ag-right-header' : undefined,
    };
  });
}

async function exportToExcel(report: ReportConfig, rows: Record<string, unknown>[]) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'RaporGor';
  wb.created = new Date();
  const ws = wb.addWorksheet(report.name.slice(0, 31));

  ws.columns = report.columns.map((c) => ({
    header: c.header,
    key: c.field,
    width: Math.max(12, Math.round((c.width ?? 120) / 7)),
  }));

  ws.getRow(1).font = { bold: true };
  ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
  ws.getRow(1).alignment = { vertical: 'middle' };

  for (const row of rows) {
    const mapped: Record<string, unknown> = {};
    for (const c of report.columns) {
      let v = row[c.field];
      if (v != null && c.type === 'date') {
        const d = dayjs(String(v));
        v = d.isValid() ? d.toDate() : v;
      }
      mapped[c.field] = v;
    }
    ws.addRow(mapped);
  }

  report.columns.forEach((c, idx) => {
    const col = ws.getColumn(idx + 1);
    if (c.type === 'currency') col.numFmt = '#,##0.00 "₺"';
    else if (c.type === 'percentage') col.numFmt = '0.00%';
    else if (c.type === 'number') col.numFmt = '#,##0.##';
    else if (c.type === 'date') col.numFmt = c.format === 'dd.MM.yyyy' ? 'dd.MM.yyyy' : 'yyyy-mm-dd';
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${report.code}-${dayjs().format('YYYYMMDD-HHmm')}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface GenericReportPageProps {
  report: ReportConfig;
}

export function GenericReportPage({ report }: GenericReportPageProps) {
  const hasDateParams = report.parameters.some((p) => p.type === 'date');
  const pivotCapable = report.code === 'satis-karlilik';
  const [pivotMode, setPivotMode] = useState(false);
  const [pivotState, setPivotState] = useState<Partial<PivotTableUIProps>>({});
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(29, 'day'),
    dayjs(),
  ]);

  const queryParams = useMemo(() => {
    if (!hasDateParams) return {};
    const params: Record<string, string> = {};
    for (const p of report.parameters) {
      if (p.type === 'date' && p.key.toLowerCase().includes('baslangic')) {
        params[p.key] = dateRange[0].format('YYYY-MM-DD');
      } else if (p.type === 'date' && p.key.toLowerCase().includes('bitis')) {
        params[p.key] = dateRange[1].format('YYYY-MM-DD');
      }
    }
    return params;
  }, [hasDateParams, dateRange, report.parameters]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['report', report.code, report.sourceId, queryParams],
    queryFn: async () => {
      if (!report.sourceId) return [];
      const res = await queryDataSource({ sourceId: report.sourceId, parameters: queryParams });
      return res.data || [];
    },
    enabled: !!report.sourceId,
  });

  const columns = useMemo(() => buildAgGridColumns(report.columns), [report.columns]);

  const aggregations = useMemo(() => {
    if (!data || data.length === 0) return [];
    return report.columns
      .filter((col) => col.agg)
      .map((col) => {
        const values = data.map((row: Record<string, unknown>) => Number(row[col.field]) || 0);
        let result = 0;
        switch (col.agg) {
          case 'sum': result = values.reduce((a: number, b: number) => a + b, 0); break;
          case 'avg': result = values.reduce((a: number, b: number) => a + b, 0) / values.length; break;
          case 'count': result = values.length; break;
          case 'min': result = Math.min(...values); break;
          case 'max': result = Math.max(...values); break;
        }
        return { label: col.header, value: formatValue(col, result) };
      });
  }, [data, report.columns]);

  const handleExport = async () => {
    try {
      await exportToExcel(report, data ?? []);
      message.success('Excel indirildi');
    } catch (e) {
      console.error(e);
      message.error('Excel oluşturulamadı');
    }
  };

  const rowCount = data?.length ?? 0;

  return (
    <div>
      {/* Başlık satırı */}
      <div style={styles.headerRow}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>{report.name}</h1>
          <TierBadge tier={report.tier} size="md" showLabel />
          <span style={styles.subtitle}>{report.category}</span>
        </div>
        <Space wrap size={8}>
          {pivotCapable && (
            <div style={styles.pivotToggle}>
              {pivotMode ? <AppstoreOutlined /> : <TableOutlined />}
              <span style={{ fontSize: 12, fontWeight: 500 }}>Pivot</span>
              <Switch size="small" checked={pivotMode} onChange={setPivotMode} />
            </div>
          )}
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} size="small">Yenile</Button>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExport}
            disabled={!data || data.length === 0}
            size="small"
          >
            Excel
          </Button>
        </Space>
      </div>

      {/* Filter chip bar */}
      <div style={styles.filterBar}>
        {hasDateParams ? (
          <div style={styles.chipRange}>
            <span style={styles.chipLabel}>Tarih</span>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates?.[0] && dates?.[1]) setDateRange([dates[0], dates[1]]);
              }}
              format="DD.MM.YYYY"
              size="small"
              variant="borderless"
              style={{ padding: 0 }}
            />
          </div>
        ) : (
          <span style={styles.chipStatic}>Tarih filtresi yok</span>
        )}
        <span style={styles.rowCount}>
          <strong className="tabular" style={{ color: 'var(--color-text-primary)' }}>{rowCount}</strong> kayıt
        </span>
      </div>

      {!report.sourceId && (
        <div style={styles.panel}>
          <Empty description="Bu rapor henüz yapılandırılmamış" style={{ padding: 32 }} />
        </div>
      )}

      {report.sourceId && !pivotMode && (
        <>
          <div style={styles.gridPanel}>
            <DataGrid
              data={data || []}
              columns={columns}
              loading={isLoading}
              height="calc(100vh - 340px)"
              pageSize={50}
            />
          </div>

          {aggregations.length > 0 && (
            <div style={styles.aggBar}>
              {aggregations.map((agg) => (
                <div key={agg.label} style={styles.aggItem}>
                  <span style={styles.aggLabel}>{agg.label}</span>
                  <strong className="tabular" style={styles.aggValue}>{agg.value}</strong>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {report.sourceId && pivotMode && pivotCapable && (
        <div style={styles.pivotPanel}>
          <PivotTableUI
            data={(data ?? []) as never}
            onChange={(s: PivotTableUIProps) => setPivotState(s)}
            {...pivotState}
          />
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  headerLeft: { display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    letterSpacing: '-0.01em',
  },
  subtitle: {
    fontSize: 11,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    fontWeight: 500,
  },
  pivotToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 10px',
    background: 'var(--color-hover-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 4,
    color: 'var(--color-text-secondary)',
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 12px',
    background: 'var(--color-card-bg)',
    border: '1px solid var(--color-border)',
    borderBottom: 'none',
    borderRadius: '4px 4px 0 0',
    fontSize: 12,
  },
  chipRange: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '2px 10px',
    background: 'var(--color-hover-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 4,
  },
  chipLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  chipStatic: {
    fontSize: 12,
    color: 'var(--color-text-muted)',
    padding: '3px 10px',
  },
  rowCount: {
    marginLeft: 'auto',
    fontSize: 12,
    color: 'var(--color-text-muted)',
  },
  panel: {
    background: 'var(--color-card-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 4,
  },
  gridPanel: {
    border: '1px solid var(--color-border)',
    borderRadius: '0 0 4px 4px',
    overflow: 'hidden',
  },
  aggBar: {
    marginTop: 10,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 20,
    padding: '10px 14px',
    background: 'var(--color-card-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 4,
  },
  aggItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  aggLabel: {
    fontSize: 10,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    fontWeight: 600,
  },
  aggValue: {
    fontSize: 14,
    color: 'var(--color-text-primary)',
    fontWeight: 700,
  },
  pivotPanel: {
    background: 'var(--color-card-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 4,
    padding: 12,
    overflow: 'auto',
  },
};
